/**
 * Re-entry for a bookmarked share view. `/share-view/<sid>` carries a session that
 * expires (4h) or goes stale (the link's settings changed); /info then answers with
 * the link token (never for a session that was logged out), and the view re-enters at
 * the link's front door (`/share/<token>`)
 * — a public link mints a fresh session, a restricted one re-asks its own gate —
 * instead of showing "This share isn't available".
 *
 * At most once per link per window: if the session the front door just minted fails
 * again, the error is shown rather than looping. Storage unavailable → no re-entry
 * (the guard can't be kept, so never risk a loop).
 */
import { PDAPP_PARAM, VIEW_PARAM, parsePdappFace } from './pdapp'
import { routeCompound } from './sessionToken'

export const SHARE_REENTRY_WINDOW_MS = 60_000

/** An open view starts checking its session this long before `exp` (client clock skew). */
export const LAPSE_MARGIN_MS = 2 * 60_000
/** …and asks /info at most this often while it does. */
export const LAPSE_PROBE_INTERVAL_MS = 30_000

const REENTRY_ERRORS = new Set(['session_invalid', 'share_config_stale'])
const keyFor = (token: string) => `papan:share-reentry:${token}`

export function claimShareReentry(
  body: { error?: unknown; shareToken?: unknown },
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  now: number,
): string | null {
  if (!storage) return null
  if (typeof body.error !== 'string' || !REENTRY_ERRORS.has(body.error)) return null
  const token = body.shareToken
  if (typeof token !== 'string' || !token) return null
  try {
    const last = Number(storage.getItem(keyFor(token)))
    if (Number.isFinite(last) && last > 0 && now - last < SHARE_REENTRY_WINDOW_MS) return null
    storage.setItem(keyFor(token), String(now))
  } catch {
    return null
  }
  return `/share/${encodeURIComponent(token)}`
}

/** A session that loaded fine ends the guard: the NEXT dead session (a later expiry) may
 *  re-enter again; only a front door that keeps minting dead sessions is stopped. */
export function clearShareReentryGuard(token: string, storage: Pick<Storage, 'removeItem'> | null): void {
  try { storage?.removeItem(keyFor(token)) } catch { /* best-effort */ }
}

/** `window.sessionStorage`, or null where reading it throws (blocked site data). */
export function sessionStorageOrNull(): Storage | null {
  try { return window.sessionStorage } catch { return null }
}

// ── Where the dead view pointed ───────────────────────────────────────────────
// The front door mints a session at the link's ROOT. The location the visitor was on
// (a folder, a file opened in place, a file's own sub-session, a PDApp face) is stashed
// in this tab before re-entering and restored once the new session lands — through the
// email gate / identity picker too, as long as it stays in this tab. Every id is
// re-checked by the server against the NEW session (/list, /sub-session), so the
// unverified payload only says where to go, never what is allowed.

/**
 * Where a share view should land on a fresh session — the ONE shape for both stashes that
 * restore a location after leaving the page: this re-entry and the /choose-identity detour
 * (ShareViewerChrome's pick context). `sub` = a session opened ON one item (a file's own
 * sub-session): `fileId` is what /sub-session is asked for (a reshare's route compound),
 * `itemId` the plain id (to recognise the item the new session already shows). `inplace` =
 * the link-root session at a location its URL names. `query` = the location params.
 */
export type ShareLandingContext =
  | { kind: 'inplace'; query: string }
  | { kind: 'sub'; fileId: string; itemId: string; query: string }
  | { kind: 'root' }

/** Generous enough for an email code round trip; a stale stash restores nothing. */
export const REENTRY_LANDING_TTL_MS = 15 * 60_000

const LOCATION_PARAMS = ['file', PDAPP_PARAM, 'parentId', VIEW_PARAM] as const
const landingKeyFor = (token: string) => `papan:share-reentry-landing:${token}`

/** The location params of a URL (anything else — `tenant`, `pick` — is identity, not place). */
export function locationQuery(params: URLSearchParams): string {
  const carry = new URLSearchParams()
  for (const k of LOCATION_PARAMS) {
    const v = params.get(k)
    if (!v) continue
    if (k === VIEW_PARAM && !parsePdappFace(v)) continue
    carry.set(k, v)
  }
  return carry.toString()
}

export function reentryLandingFrom(
  payload: { fileId?: string; xt?: boolean; xtm?: string; xtr?: string[] } | null,
  params: URLSearchParams,
): ShareLandingContext {
  const query = locationQuery(params)
  // The item the dead session was opened on. Whether that was the link root (nothing to
  // re-open) is only known once the new session lands — see resolveReentryLanding.
  if (payload?.fileId && !params.get('file')) {
    const fileId = payload.xt && payload.xtm ? routeCompound(payload, payload.fileId) : payload.fileId
    return { kind: 'sub', fileId, itemId: payload.fileId, query }
  }
  return query ? { kind: 'inplace', query } : { kind: 'root' }
}

/**
 * The landing from the SERVER's answer for a dead session (`landing` on /info's 404/409 —
 * the server reads the token, which a v2 token seals from the client). Same rule as
 * reentryLandingFrom: a `?file=` open in place wins, else the item the session was opened on.
 */
export function reentryLandingFromServer(
  landing: { fileId?: unknown; itemId?: unknown } | null | undefined,
  params: URLSearchParams,
): ShareLandingContext | null {
  if (!landing || typeof landing.fileId !== 'string' || typeof landing.itemId !== 'string' || !landing.fileId) return null
  const query = locationQuery(params)
  if (!params.get('file')) return { kind: 'sub', fileId: landing.fileId, itemId: landing.itemId, query }
  return query ? { kind: 'inplace', query } : { kind: 'root' }
}

/** A `sub` landing on the item the NEW session already shows is just its query. */
export function resolveReentryLanding(ctx: ShareLandingContext, landedItemId: string): ShareLandingContext {
  if (ctx.kind !== 'sub' || ctx.itemId !== landedItemId) return ctx
  return ctx.query ? { kind: 'inplace', query: ctx.query } : { kind: 'root' }
}

/** Validate a landing read back from storage (any shape — it is data). Accepts the
 *  pre-unification pick shape `{ kind: 'sub', fileId, face }` too. */
export function parseLandingContext(v: unknown): ShareLandingContext | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (o.kind === 'root') return { kind: 'root' }
  if (o.kind === 'inplace' && typeof o.query === 'string') return { kind: 'inplace', query: o.query }
  if (o.kind === 'sub' && typeof o.fileId === 'string' && o.fileId) {
    let query = typeof o.query === 'string' ? o.query : ''
    if (!query && typeof o.face === 'string' && parsePdappFace(o.face)) query = `${VIEW_PARAM}=${o.face}`
    return { kind: 'sub', fileId: o.fileId, itemId: typeof o.itemId === 'string' ? o.itemId : o.fileId, query }
  }
  return null
}

export function stashReentryLanding(
  token: string,
  ctx: ShareLandingContext,
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  now: number,
): void {
  if (!storage) return
  try {
    if (ctx.kind === 'root') storage.removeItem(landingKeyFor(token))
    else storage.setItem(landingKeyFor(token), JSON.stringify({ at: now, ctx }))
  } catch { /* best-effort: the visitor lands on the link root */ }
}

/** The stashed landing for this link — read AND cleared (one-shot); null when absent / stale. */
export function takeReentryLanding(
  token: string,
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null,
  now: number,
): ShareLandingContext | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(landingKeyFor(token))
    if (!raw) return null
    storage.removeItem(landingKeyFor(token))
    const { at, ctx } = JSON.parse(raw) as { at?: unknown; ctx?: unknown }
    if (typeof at !== 'number' || now - at > REENTRY_LANDING_TTL_MS) return null
    const parsed = parseLandingContext(ctx)
    return parsed && parsed.kind !== 'root' ? parsed : null
  } catch {
    return null
  }
}
